/**
 * Session / Elections mode for the Pro home page.
 *
 * The default is derived from the calendar, not the user: in session → session;
 * otherwise within 120 days before (or 14 after) the general election →
 * elections; otherwise session. A manual toggle overrides the default and
 * persists per browser (localStorage) so an interim campaign user doesn't
 * re-flip it every visit.
 */
import { useCallback, useState } from 'react';
import { daysUntilLocalDate } from '../../../utils/dateUtils';

export type HomeMode = 'session' | 'elections';

const STORAGE_KEY = 'proHome.mode';

function readStoredMode(): HomeMode | null {
  try {
    const value = localStorage.getItem(STORAGE_KEY);
    return value === 'session' || value === 'elections' ? value : null;
  } catch {
    return null;
  }
}

export function resolveDefaultMode(
  hasSessionEventsSoon: boolean,
  generalDate: string | null,
): HomeMode {
  if (hasSessionEventsSoon) return 'session';
  if (generalDate) {
    // Same tz-safe countdown as the visible cards (see utils/dateUtils) — the
    // parseISO form flipped the session/elections mode a day early.
    const days = daysUntilLocalDate(generalDate);
    if (days === null) return 'session';
    if (days >= -14 && days <= 120) return 'elections';
  }
  return 'session';
}

export function useHomeMode(hasSessionEventsSoon: boolean, generalDate: string | null) {
  const [override, setOverride] = useState<HomeMode | null>(readStoredMode);

  const mode: HomeMode = override ?? resolveDefaultMode(hasSessionEventsSoon, generalDate);

  const setMode = useCallback((next: HomeMode) => {
    setOverride(next);
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // Storage unavailable — the toggle still works for this visit.
    }
  }, []);

  return { mode, setMode };
}
